import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { apiClient, ApiClientError, unwrap, type ApiSuccess } from '@/lib/api-client';
import type { CurrentUser, EfsRole } from '@/lib/permissions';

/**
 * Auth hooks. Wire format from `06-ui-api §5 Auth/User`:
 *
 *   GET  /api/me                 -> { data: UserDto } | 401
 *   POST /api/auth/login         -> { data: UserDto }
 *   POST /api/auth/logout        -> { data: null }
 *
 * Note: the spec table uses `/api/login`; task contract aligns the frontend
 * with the eventual `/api/auth/*` namespace so refactor stays cheap.
 */

interface UserDto {
  user_id: string;
  email: string;
  display_name: string;
  roles: EfsRole[];
  permissions?: string[];
  facility_id?: string | null;
  company_id?: string | null;
  party_type?: string | null;
  simulated_by?: {
    user_id: string;
    email: string;
    display_name: string;
  } | null;
}

const ME_KEY = ['auth', 'me'] as const;

const mapUser = (dto: UserDto): CurrentUser => ({
  id: dto.user_id,
  username: dto.email,
  email: dto.email,
  displayName: dto.display_name,
  roles: dto.roles ?? [],
  permissions: dto.permissions ?? [],
  facilityId: dto.facility_id ?? null,
  companyId: dto.company_id ?? null,
  partyType: dto.party_type ?? null,
  simulatedBy: dto.simulated_by
    ? {
        id: dto.simulated_by.user_id,
        email: dto.simulated_by.email,
        displayName: dto.simulated_by.display_name,
      }
    : null,
});

export const useMe = (): UseQueryResult<CurrentUser | null, ApiClientError> => {
  return useQuery<CurrentUser | null, ApiClientError>({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        const dto = await unwrap(apiClient.get<ApiSuccess<UserDto>>('/me'));
        return mapUser(dto);
      } catch (err) {
        // 401 is the unauthenticated signal — treat as "no user" without
        // surfacing as an error so the RequireAuth gate handles redirect.
        if (err instanceof ApiClientError && err.status === 401) {
          return null;
        }
        throw err;
      }
    },
    retry: (failureCount, error) => {
      if (error instanceof ApiClientError && error.status === 401) return false;
      return failureCount < 1;
    },
    staleTime: 60_000,
  });
};

export interface LoginInput {
  email: string;
  password: string;
}

export const useLogin = () => {
  const qc = useQueryClient();
  return useMutation<CurrentUser, ApiClientError, LoginInput>({
    mutationFn: async (input) => {
      const dto = await unwrap(apiClient.post<ApiSuccess<UserDto>>('/auth/login', input));
      return mapUser(dto);
    },
    onSuccess: (user) => {
      qc.setQueryData(ME_KEY, user);
    },
  });
};

export const useLogout = () => {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, void>({
    mutationFn: async () => {
      await apiClient.post('/auth/logout', {});
    },
    onSuccess: () => {
      qc.setQueryData(ME_KEY, null);
      qc.removeQueries();
    },
  });
};

export const useStartPermissionSimulation = () => {
  const qc = useQueryClient();
  return useMutation<CurrentUser, ApiClientError, { userId: string }>({
    mutationFn: async ({ userId }) => {
      const dto = await unwrap(
        apiClient.post<ApiSuccess<UserDto>>('/auth/simulation', { user_id: userId }),
      );
      return mapUser(dto);
    },
    onSuccess: (user) => {
      qc.setQueryData(ME_KEY, user);
      qc.invalidateQueries();
    },
  });
};

export const useStopPermissionSimulation = () => {
  const qc = useQueryClient();
  return useMutation<CurrentUser, ApiClientError, void>({
    mutationFn: async () => {
      const dto = await unwrap(apiClient.delete<ApiSuccess<UserDto>>('/auth/simulation'));
      return mapUser(dto);
    },
    onSuccess: (user) => {
      qc.setQueryData(ME_KEY, user);
      qc.invalidateQueries();
    },
  });
};
